// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import moment from "moment";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { Button, Col, message, Popconfirm, Space, Typography } from "antd";
import Table, { ColumnsType } from "antd/lib/table";
import {
  UndoOutlined,
  StopOutlined,
  CheckCircleOutlined,
  UnlockOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";

import {
  selectOrderForUpdate,
  selectOrderPaymentConfirmations,
} from "../../../../redux/selectors/orders.selectors";
import {
  ordersService,
  paymentConfirmationsService,
  ipAccessRulesService,
} from "../../../../shared/services";
import useAppDispatch from "../../../../redux/hooks/useAppDispatch";
import {
  changeOrderPaymentConfirmations,
  setOrdersForUpdate,
} from "../../../../redux";
import { decimal } from "../../../../shared/utils/Helper";

const { Title } = Typography;

export const PaymentConfirmation = () => {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const orderForUpdate = useSelector(selectOrderForUpdate);
  const OrderPaymentConfirmations = useSelector(
    selectOrderPaymentConfirmations
  );
  const [isLoading, setIsLoading] = useState(false);

  const getRefreshSingleOrder = async () => {
    if (isLoading) return;
    setIsLoading(true);
    return await ordersService
      .get(Number(id))
      .then((res) => {
        setIsLoading(false);
        return dispatch(setOrdersForUpdate(res));
      })
      .catch((err: any) => {
        console.log(err);
        setIsLoading(false);
        return message.error(err.message);
      });
  };

  const getArrOfPaymentConfirmations = async () => {
    await paymentConfirmationsService
      .find({
        query: { order_id: orderForUpdate?.id, $limit: 9999999999999 },
      })
      .then((res: any) => dispatch(changeOrderPaymentConfirmations(res.data)));
  };

  const blockIp = async (ip: string) => {
    if (!ip) return message.warning("No hay IP para bloquear");
    try {
      await ipAccessRulesService.create({
        ip,
        type: "block",
        source: "manual",
        status: "active",
        reason: `Bloqueo manual desde pedido #${orderForUpdate?.id}`,
        related_user_id: orderForUpdate?.user_id || null,
      });
      message.success(`IP ${ip} bloqueada`);
    } catch (err: any) {
      message.error(err?.message || "No se pudo bloquear la IP");
    }
  };

  const whitelistIp = async (ip: string) => {
    if (!ip) return message.warning("No hay IP para lista blanca");
    try {
      await ipAccessRulesService.create({
        ip,
        type: "allow",
        source: "manual",
        status: "active",
        reason: `Lista blanca desde pedido #${orderForUpdate?.id}`,
        related_user_id: orderForUpdate?.user_id || null,
      });
      message.success(`IP ${ip} agregada a lista blanca`);
    } catch (err: any) {
      message.error(err?.message || "No se pudo agregar a lista blanca");
    }
  };

  const unblockIp = async (ip: string) => {
    if (!ip) return message.warning("No hay IP para desbloquear");
    try {
      const res: any = await ipAccessRulesService.find({
        query: {
          ip,
          type: "block",
          status: "active",
          $limit: 50,
        },
      });
      const rules = res?.data || [];
      if (!rules.length) {
        message.info("No hay bloqueos activos para esa IP");
        return;
      }
      await Promise.all(
        rules.map((rule: any) =>
          ipAccessRulesService.patch(rule.id, { status: "inactive" })
        )
      );
      message.success(`IP ${ip} desbloqueada`);
    } catch (err: any) {
      message.error(err?.message || "No se pudo desbloquear la IP");
    }
  };

  useEffect(() => {
    getArrOfPaymentConfirmations();
  }, []);

  const columns: ColumnsType<any> = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Referencia",
      dataIndex: "reference",
      key: "reference",
    },
    {
      title: "Valor",
      dataIndex: "value",
      key: "value",
      render: (_, item) => {
        return (
          <Col
            style={{
              display: "flex",
              width: "100%",
              flexDirection: "row",
            }}
          >
            <Title style={{ fontSize: "0.74rem", fontWeight: "500" }} level={5}>
              $ {decimal(item.value)}
            </Title>
          </Col>
        );
      },
    },
    {
      title: "Estado",
      dataIndex: "status",
      key: "status",
    },
    {
      title: "Respuesta",
      dataIndex: "response",
      key: "response",
    },
    {
      title: "Código respuesta",
      dataIndex: "responseCode",
      key: "responseCode",
    },
    {
      title: "Banco",
      dataIndex: "bank",
      key: "bank",
    },
    {
      title: "Franquicia",
      dataIndex: "franchise",
      key: "franchise",
    },
    {
      title: "Ip",
      dataIndex: "ip",
      key: "ip",
      render: (_, item) => item.ip || orderForUpdate?.client_ip || "—",
    },
    {
      title: "Acciones IP",
      key: "ip_actions",
      render: (_, item) => {
        const ip = item.ip || orderForUpdate?.client_ip;
        if (!ip) return "—";
        return (
          <Space size="small" wrap>
            <Popconfirm
              title={`¿Bloquear IP ${ip}?`}
              onConfirm={() => blockIp(ip)}
              okText="Sí"
              cancelText="No"
            >
              <Button size="small" danger icon={<StopOutlined />}>
                Bloquear
              </Button>
            </Popconfirm>
            <Popconfirm
              title={`¿Desbloquear IP ${ip}?`}
              onConfirm={() => unblockIp(ip)}
              okText="Sí"
              cancelText="No"
            >
              <Button size="small" icon={<UnlockOutlined />}>
                Desbloquear
              </Button>
            </Popconfirm>
            <Popconfirm
              title={`¿Pasar ${ip} a lista blanca?`}
              onConfirm={() => whitelistIp(ip)}
              okText="Sí"
              cancelText="No"
            >
              <Button size="small" type="primary" icon={<CheckCircleOutlined />}>
                Lista blanca
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
    {
      title: "Fecha",
      dataIndex: "date",
      key: "date",
      render: (_, item) => (
        <Col
          style={{
            display: "flex",
            width: "100%",
            justifyContent: "space-evenly",
            flexDirection: "row",
          }}
        >
          <Title level={5}>
            {moment(item.createdAt).format("DD/MM/YYYY HH:mm:ss")}
          </Title>
        </Col>
      ),
    },
  ];

  return (
    <Col
      span={24}
      style={{
        width: "100%",
        display: "flex",
        margin: "auto",
        justifyContent: "flex-start",
        alignItems: "flex-end",
        flexDirection: "column",
      }}
    >
      <Button
        style={{ marginBottom: "5px" }}
        type="primary"
        shape="circle"
        onClick={getRefreshSingleOrder}
        disabled={isLoading}
        icon={<UndoOutlined />}
      />
      <Table
        style={{ width: "100%" }}
        pagination={false}
        bordered
        columns={columns}
        dataSource={OrderPaymentConfirmations}
      />
    </Col>
  );
};
